import React, { useState, useCallback, useRef, useEffect } from "react";
import ReferenceListCard from "../components/Settings/ProductSettings/ReferenceListCard";
import { useProductTypes } from "../hooks/useProductTypes";
import { useFabricTypes } from "../hooks/useFabricTypes";
import { useProductFormations } from "../hooks/useProductFormations";
import { useDeliveryChannels } from "../hooks/useDeliveryChannels";
import { useStockLocations } from "../hooks/useStockLocations";
import { useFabrics } from "../hooks/useFabrics";
import "../components/Settings/ProductSettings/ProductSettings.css";

const ProductSettings = () => {
  // -- data hooks -------------------------------------------------------------
  const { productTypes, productTypesLoading, fetchProductTypes } =
    useProductTypes();
  const { fabricTypes, fabricTypesLoading, fetchFabricTypes } =
    useFabricTypes();
  const { productFormations, productFormationsLoading, fetchProductFormations } =
    useProductFormations();
  const { deliveryChannels, deliveryChannelsLoading, fetchDeliveryChannels } =
    useDeliveryChannels();
  const { stockLocations, stockLocationsLoading, fetchStockLocations } =
    useStockLocations();
  const { fabrics, fabricsLoading, fetchFabrics, loadMore, hasMore, totalCount: fabricsTotal } =
    useFabrics();

  // -- global filter ----------------------------------------------------------
  const [filterText, setFilterText] = useState("");
  const filterInputRef = useRef(null);

  // Keyboard shortcut "/" to focus the global filter
  useEffect(() => {
    const handler = (e) => {
      if (
        e.key === "/" &&
        document.activeElement !== filterInputRef.current &&
        !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)
      ) {
        e.preventDefault();
        filterInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // -- fabric colours server search -------------------------------------------
  const [fabricSearch, setFabricSearch] = useState("");
  const fabricSearchTimeout = useRef(null);

  const handleFabricSearch = useCallback(
    (value) => {
      setFabricSearch(value);
      if (fabricSearchTimeout.current) clearTimeout(fabricSearchTimeout.current);
      fabricSearchTimeout.current = setTimeout(() => {
        fetchFabrics(1, value || null);
      }, 400);
    },
    [fetchFabrics]
  );

  // -- stats ------------------------------------------------------------------
  const ft = filterText.trim().toLowerCase();

  const allLists = [
    { key: "fabricTypes", data: fabricTypes, serverSearch: false },
    { key: "productTypes", data: productTypes, serverSearch: false },
    { key: "productFormations", data: productFormations, serverSearch: false },
    { key: "deliveryChannels", data: deliveryChannels, serverSearch: false },
    { key: "stockLocations", data: stockLocations, serverSearch: false },
  ];

  const totalRecords =
    allLists.reduce((sum, l) => sum + l.data.length, 0) + fabrics.length;
  const totalLists = 6;

  const isFilterActive = ft.length > 0;

  return (
    <div className="ps-page">
      {/* ---- header ---- */}
      <div className="ps-header">
        <div className="ps-header-left">
          <h1>Product Settings</h1>
          <p>
            Reference lists used by the order form and ready stock. Names only —
            add, rename or remove in place.
          </p>
        </div>
        <div className="ps-header-right">
          <div className="ps-global-filter">
            <span className="ps-filter-icon">⌕</span>
            <input
              ref={filterInputRef}
              type="text"
              placeholder="Filter all lists"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
            <span className="ps-filter-shortcut">/</span>
          </div>
          <div className="ps-stats-badge">
            <span className="ps-stats-num">{totalRecords}</span> records ·{" "}
            <span className="ps-stats-num">{totalLists}</span> lists
          </div>
        </div>
      </div>

      {/* ---- active filter chip ---- */}
      {isFilterActive && (
        <div className="ps-filter-bar">
          <span className="ps-filter-bar-label">Filter active</span>
          <span className="ps-filter-chip">
            <span className="ps-filter-chip-icon">⌕</span>
            {filterText}
            <button
              className="ps-filter-chip-clear"
              onClick={() => setFilterText("")}
            >
              ✕
            </button>
          </span>
        </div>
      )}

      {/* ---- grid ---- */}
      <div className="ps-grid">
        {/* 1. Fabric Types */}
        <ReferenceListCard
          settingsKey="fabricTypes"
          title="Fabric Types"
          data={fabricTypes}
          loading={fabricTypesLoading}
          fetch={fetchFabricTypes}
          filterText={ft ? filterText : ""}
          totalCount={fabricTypes.length}
        />

        {/* 2. Product Types */}
        <ReferenceListCard
          settingsKey="productTypes"
          title="Product Types"
          data={productTypes}
          loading={productTypesLoading}
          fetch={fetchProductTypes}
          filterText={ft ? filterText : ""}
          totalCount={productTypes.length}
        />

        {/* 3. Product Formations */}
        <ReferenceListCard
          settingsKey="productFormations"
          title="Product Formations"
          data={productFormations}
          loading={productFormationsLoading}
          fetch={fetchProductFormations}
          filterText={ft ? filterText : ""}
          totalCount={productFormations.length}
        />

        {/* 4. Delivery Channels */}
        <ReferenceListCard
          settingsKey="deliveryChannels"
          title="Delivery Channels"
          data={deliveryChannels}
          loading={deliveryChannelsLoading}
          fetch={fetchDeliveryChannels}
          filterText={ft ? filterText : ""}
          totalCount={deliveryChannels.length}
        />

        {/* 5. Stock Locations */}
        <ReferenceListCard
          settingsKey="stockLocations"
          title="Stock Locations"
          data={stockLocations}
          loading={stockLocationsLoading}
          fetch={fetchStockLocations}
          filterText={ft ? filterText : ""}
          totalCount={stockLocations.length}
        />

        {/* 6. Fabric Colours (special: server search, no rename, load more) */}
        <ReferenceListCard
          settingsKey="fabricColours"
          title="Fabric Colours"
          data={fabrics}
          loading={fabricsLoading}
          fetch={fetchFabrics}
          loadMore={loadMore}
          hasMore={hasMore}
          totalCount={fabricsTotal || fabrics.length}
          serverSearch
          filterText={fabricSearch}
          onFilterChange={handleFabricSearch}
        />
      </div>

      <p className="ps-footer-text">
        Five lists load in full and filter client-side. Fabric Colours paginates
        from the server, so its search sits in its own header.
      </p>
    </div>
  );
};

export default ProductSettings;
